import { useEffect, useState } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';

interface SubjectSphereEntry {
  identityId: string;
  subjectSphereId: string | null;
}

/** The graph route caps one batch at 50 identity ids. */
const BATCH_LIMIT = 50;

/**
 * Resolves identity ids (what the server records as an actor, e.g. a rollup exclusion's `excludedBy`) to
 * their subject sphere ids, which is what `useUserData`/`useMultipleUserData` can name. An identity the
 * caller may not discover, or that has no subject sphere, is absent from the result.
 */
export function useIdentitySubjectSpheres(identityIds: string[]): Record<string, string> {
  const apiClients = useApiClientsOrNull();
  const [sphereByIdentity, setSphereByIdentity] = useState<Record<string, string>>({});
  const key = Array.from(new Set(identityIds.filter(Boolean))).sort((a, b) => a.localeCompare(b)).join(',');

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0 || !apiClients) {
      setSphereByIdentity({});
      return;
    }

    let cancelled = false;
    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH_LIMIT) batches.push(ids.slice(i, i + BATCH_LIMIT));

    Promise.all(
      batches.map((batch) =>
        apiClients.graphClient.post('/identities/batch-subject-spheres', { identityIds: batch }) as Promise<
          SubjectSphereEntry[]
        >
      )
    )
      .then((results) => {
        if (cancelled) return;
        const resolved: Record<string, string> = {};
        for (const entry of results.flat()) {
          if (entry?.subjectSphereId) resolved[entry.identityId] = entry.subjectSphereId;
        }
        setSphereByIdentity(resolved);
      })
      .catch(() => {
        // Unresolved names degrade to the by-less line; a name is never invented.
        if (!cancelled) setSphereByIdentity({});
      });

    return () => {
      cancelled = true;
    };
  }, [key, apiClients]);

  return sphereByIdentity;
}
